import React, { useState, useMemo } from 'react';
import { Check } from 'lucide-react';
import { tabConfig } from "../constants/tabConfig";
import { ExcludedItems } from '../types/CourseData';
// Define CourseTabProps  
interface CourseTabProps {
  activeSubTab: keyof typeof tabConfig;
  displayName: string;
  matchSubTab: string;
  visibility: Record<string, boolean>;
  items: any[];
  itemCount: number;
  handleTabChange: (key: string) => void;
  handleCheckboxChange: (id: string, type: string, courseId: number,idField: string ) => void;
  checkedItems: string[];
  courseId: number;
  excludedItems: ExcludedItems;
  updateExcludedItems: (courseId: number, type: string, itemId: string) => void;
  selectedItems: Record<string, boolean>;
}
const ITEMS_PER_PAGE = 10;

const CanvasCourseTab: React.FC<CourseTabProps> = ({
  activeSubTab,
  displayName,
  matchSubTab,
  visibility,
  items,
  itemCount,
  handleTabChange,
  handleCheckboxChange,
  checkedItems,
  courseId,
  excludedItems,
  updateExcludedItems,
  selectedItems,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1); 
  const [pageSize, setPageSize] = useState(10);
  const { label, key, idField, nameField } = tabConfig[activeSubTab];


  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const itemName = nameField.split('.').reduce((obj, key) => obj?.[key], item);
      return itemName?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [items, searchTerm]);

  // ✅ Early return happens **after** all hooks are called
  if (!tabConfig[activeSubTab] || displayName !== matchSubTab) return null;

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = filteredItems.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleItemCheckboxChange = (itemId: string, type: string) => {
    handleCheckboxChange(itemId, key, courseId ?? 0, idField);
    const excludedKey = `excluded_${type}`;
    updateExcludedItems(courseId, excludedKey, itemId);
  };

  return (
    <div>
      <input
        type="text"
        placeholder="Search..."
        className="border p-2 mb-4 w-full"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {filteredItems.length === 0 ? (
        <h3 className="text-xl font-semibold mb-4">No data found</h3>
      ) : (
        <>
          <table className="w-full border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border text-center p-2">Title</th>
                <th className="border text-center p-2">Publish Status</th>
                <th className="border text-center p-2">Available On</th>
                <th className="border text-center p-2">Available Until</th>
                <th className="border text-center p-2">Posted On</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item) => {
                const itemId = item[idField] ?? "";
                const itemName = nameField.split('.').reduce((obj, key) => obj?.[key], item);

                return (
                  <tr key={itemId} className="border">
                    <td className="border text-left p-2 w-[35%]">
                      <div className="flex items-center">
                        {/* ✅ Ensures consistent checkbox size */}
                        <div className="flex-shrink-0 h-6 w-6">
                          <div
                            className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
                              checkedItems.includes(itemId) ? 'bg-black' : 'bg-white'
                            }`}
                            onClick={() => handleItemCheckboxChange(itemId, key)}
                            id={`checkbox-${itemId}`}
                          >
                            {checkedItems.includes(itemId) && <Check className="h-4 w-4 text-white" />}
                          </div>
                        </div>

                        {/* ✅ Title wraps correctly without affecting checkbox size */}
                        <label className="text-gray pl-4 break-words w-full" htmlFor={`checkbox-${itemId}`}>
                          {itemName}
                        </label>
                      </div>
                    </td>
                    <td className="border text-center p-2 w-[10%]">{item.published ? "Published" : "Unpublished"}</td>
                    <td className="border text-center p-2 w-[20%]">{item?.will_become_available_on || "N/A"}</td>
                    <td className="border text-center p-2 w-[20%]">{item?.due_at || "N/A"}</td>
                    <td className="border text-center p-2 w-[20%]">{item?.posted_at || "N/A"}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex justify-center items-center gap-2 mt-6 mb-4">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => {
                const pageNumber = i + 1;
                const isFirst = pageNumber === 1;
                const isLast = pageNumber === totalPages;
                const isInRange = pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2;

                    // Render first page, last page, or numbers around the current page
                    if (isFirst || isLast || isInRange) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-1 border rounded-md ${currentPage === pageNumber ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'}`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }

                    // Render ellipsis when there's a gap
                    if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                      return <span key={`ellipsis-${pageNumber}`}>...</span>;
                    }

                    return null;
                  })}


                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CanvasCourseTab;
